import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api.js';
import { formatBytes, formatDate } from './adminUtils.js';

interface PracticeMapMeta {
  url: string;
  filename: string;
  size: number;
  uploadedAt: string;
}

export default function AdminPracticeMapTab() {
  const [meta, setMeta] = useState<PracticeMapMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [successMsg, setSuccessMsg] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<PracticeMapMeta | null>('/api/admin/practice-map');
      setMeta(data);
    } catch (err) {
      console.error('Failed to load practice map:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const uploadFile = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setError('File must be a .zip archive');
      return;
    }

    setError('');
    setSuccessMsg('');
    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('map', file);

    try {
      const { data } = await api.post<PracticeMapMeta & { success: boolean; message: string }>(
        '/api/admin/practice-map',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        }
      );
      setMeta({
        url: data.url,
        filename: data.filename,
        size: data.size,
        uploadedAt: data.uploadedAt
      });
      setSuccessMsg('Practice map uploaded successfully');
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Upload failed';
      setError(message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) void uploadFile(file);
  };

  if (loading) {
    return <div className="h-48 bg-surface-variant rounded-lg animate-pulse" />;
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {meta && (
        <div className="bg-[#141414] thin-border rounded-lg p-6">
          <h3 className="font-label-caps text-label-caps text-on-surface-variant uppercase mb-4">
            Current practice map
          </h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-on-surface-variant">Filename</dt>
              <dd className="text-on-surface font-medium">{meta.filename}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-on-surface-variant">Upload date</dt>
              <dd className="text-on-surface">{formatDate(meta.uploadedAt)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-on-surface-variant">File size</dt>
              <dd className="text-on-surface">{formatBytes(meta.size)}</dd>
            </div>
          </dl>
        </div>
      )}

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="border-2 border-dashed border-outline-variant rounded-xl p-12 text-center hover:border-error/50 transition-colors cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
      >
        <span className="material-symbols-outlined text-4xl text-on-surface-variant mb-3">upload_file</span>
        <p className="text-on-surface font-medium mb-1">Drop practice map ZIP here</p>
        <p className="text-on-surface-variant text-sm">or click to browse</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".zip"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void uploadFile(file);
            e.target.value = '';
          }}
        />
      </div>

      {uploading && (
        <div>
          <div className="flex justify-between text-xs text-on-surface-variant mb-1">
            <span>Uploading…</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="h-2 bg-surface-variant rounded-full overflow-hidden">
            <div
              className="h-full bg-error transition-all duration-150"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {successMsg && (
        <p className="text-green-500 text-sm border border-green-500/40 rounded px-3 py-2">{successMsg}</p>
      )}
      {error && <p className="text-error text-sm">{error}</p>}
    </div>
  );
}
